import ComputerIcon from '@mui/icons-material/Computer';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, Typography, CircularProgress } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';

interface PlanSelectionCardsProps {
    onSelectDiy: () => void;
    onSelectExpert: () => void;
    expertDisabled?: boolean;
    isDiyLoading?: boolean;
}

const PlanSelectionCards: React.FC<PlanSelectionCardsProps> = ({
    onSelectDiy,
    onSelectExpert,
    expertDisabled = false,
    isDiyLoading = false,
}) => {
    const { t } = useTranslation();

    // Modern card styles for both options
    const optionBoxStyle = {
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        position: 'relative',
        minHeight: '200px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
            borderColor: 'primary.main',
            '& .option-icon': {
                transform: 'scale(1.1)',
                color: 'primary.main',
            },
            '& .option-title': {
                color: 'primary.main',
            },
        },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: '800px',
                mx: 'auto',
                py: 4,
            }}
        >
            <Typography
                variant="h4"
                component="h1"
                sx={{
                    fontWeight: 600,
                    mb: 4,
                    textAlign: 'center',
                    color: 'text.primary',
                }}
            >
                {t('plans.initialOptions.title')}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    gap: 4,
                    width: '100%',
                    maxWidth: '700px',
                    mx: 'auto',
                }}
            >
                {/* DIY Option */}
                <Box 
                    sx={{
                        ...optionBoxStyle,
                        opacity: isDiyLoading ? 0.7 : 1,
                        cursor: isDiyLoading ? 'not-allowed' : 'pointer',
                        pointerEvents: isDiyLoading ? 'none' : 'auto',
                        '&:hover': isDiyLoading ? {} : optionBoxStyle['&:hover'],
                    }} 
                    onClick={() => {
                        if (!isDiyLoading) {
                            onSelectDiy();
                        }
                    }}
                >
                    <Box
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{ mb: 3, position: 'relative' }}>
                            {isDiyLoading ? (
                                <CircularProgress 
                                    sx={{ 
                                        fontSize: '3rem',
                                        color: 'primary.main',
                                    }}
                                />
                            ) : (
                                <ComputerIcon
                                    className="option-icon"
                                    sx={{
                                        fontSize: '3rem',
                                        color: 'text.secondary',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                />
                            )}
                        </Box>
                        <Typography
                            variant="h5"
                            className="option-title"
                            sx={{
                                fontWeight: 'bold',
                                mb: 2,
                                color: 'text.primary',
                                transition: 'color 0.3s ease',
                            }}
                        >
                            {t('plans.initialOptions.doItYourself')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: '1rem',
                            }}
                        >
                            {t('plans.initialOptions.doItYourselfDescription')}
                        </Typography>
                    </Box>
                </Box>

                {/* Expert Option */}
                <Box
                    sx={{
                        ...optionBoxStyle,
                        opacity: expertDisabled ? 0.5 : 1,
                        cursor: expertDisabled ? 'default' : 'pointer',
                        '&:hover': expertDisabled
                            ? {
                                  transform: 'none',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                  borderColor: 'divider',
                                  '& .option-icon': {
                                      transform: 'none',
                                      color: 'text.secondary',
                                  },
                                  '& .option-title': {
                                      color: 'text.primary',
                                  },
                              }
                            : optionBoxStyle['&:hover'],
                    }}
                    onClick={() => {
                        if (!expertDisabled) {
                            onSelectExpert();
                        }
                    }}
                >
                    <Box
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <SupportAgentIcon
                                className="option-icon"
                                sx={{
                                    fontSize: '3rem',
                                    color: 'text.secondary',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h5"
                            className="option-title"
                            sx={{
                                fontWeight: 'bold',
                                mb: 2,
                                color: 'text.primary',
                                transition: 'color 0.3s ease',
                            }}
                        >
                            {t('plans.initialOptions.withExpert')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: '1rem',
                            }}
                        >
                            {t('plans.initialOptions.withExpertDescription')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PlanSelectionCards;
